import { VitalsMetadata } from '../../../../models/form-widget-emr-vitals.model';

function toNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toFiniteOrNull(v: unknown): number | null {
  if (v === null || v === undefined) {
    return null;
  }

  if (typeof v === 'string' && v.trim() === '') {
    return null;
  }

  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function round(value: number, digits: number): number {
  const p = Math.pow(10, digits);
  return Math.round(value * p) / p;
}

/**
 * Calculates Body Mass Index (BMI) from vitals data using imperial units (lbs, ft/in).
 *
 * Formula (imperial):
 *   BMI = (weight_lbs / height_in^2) * 703
 *
 * Where:
 * - weight_lbs is the body weight in pounds (lbs)
 * - height_in is the height in total inches (height_ft * 12 + height_in)
 * - 703 is the imperial conversion factor that aligns the result with the standard BMI scale
 *
 * Backend parity:
 * - Matches the imperial BMI calculation in WeightLossItem.CalculateStats():
 *   BMI = Math.Round((WeightLbs / (heightInInches * heightInInches)) * 703, 1)
 *
 * Rounding:
 * - Rounded to 1 decimal place to match backend behavior (C# Math.Round(value, 1)).
 *
 * Validation / Edge cases:
 * - If height is 0 or negative, BMI cannot be computed (division by zero / invalid input).
 * - If weight is 0 or negative, BMI is not meaningful.
 * - Returns null when the calculation cannot be performed due to invalid/insufficient inputs.
 *
 * Notes:
 * - This function assumes the widget is imperial-only: height is stored as feet/inches and weight as pounds.
 * - `toNum()` is used to safely coerce null/undefined/string values to numbers (non-finite values become 0).
 *
 * @param data Vitals metadata containing height (height_ft/height_in) and weight (weight) in lbs.
 * @returns BMI rounded to 1 decimal place, or null if inputs are invalid.
 */
export function calcBmiImperial(data: VitalsMetadata): number | null {
  const ft = toNum(data.height_ft);
  const inch = toNum(data.height_in);
  const lbs = toNum(data.weight);

  // Convert height to total inches: 1 ft = 12 in
  const heightInInches = ft * 12 + inch;

  if (heightInInches <= 0 || lbs <= 0) {
    return null;
  }

  return round((lbs / (heightInInches * heightInInches)) * 703, 1);
}

/**
 * Calculates the weight change since the previous visit.
 *
 * Meaning:
 * - Positive result  => current weight is higher than the previous visit
 * - Negative result  => current weight is lower than the previous visit
 * - Zero             => no change
 *
 * Data requirements:
 * - `data.weight` must be a finite number (current visit weight).
 * - `data.prev_weight` must be a finite number (previous visit weight).
 *
 * Validation / Edge cases:
 * - If either value is missing, null, NaN, or not a finite number, the function returns null.
 *   Returning null signals "cannot calculate" (insufficient/invalid input).
 *
 * Units:
 * - The result is in the same units as the stored weights (in your widget this is typically lbs).
 *
 * @param data Vitals metadata containing current and previous weights.
 * @returns Difference: currentWeight - previousWeight, or null if not calculable.
 */
export function calcLastVisitDiff(data: VitalsMetadata): number | null {
  const weight = toFiniteOrNull(data.weight);
  const prevWeight = toFiniteOrNull(data.prev_weight);

  if (weight === null || prevWeight === null) {
    return null;
  }

  return weight - prevWeight;
}

/**
 * Calculates the weight change since the starting weight (baseline).
 *
 * Meaning:
 * - Positive result  => current weight is higher than the starting weight
 * - Negative result  => current weight is lower than the starting weight
 * - Zero             => no change
 *
 * Data requirements:
 * - `data.weight` must be a finite number (current visit weight).
 * - `data.start_weight` must be a finite number (baseline/starting weight).
 *
 * Validation / Edge cases:
 * - If either value is missing, null, NaN, or not a finite number, the function returns null.
 *   Returning null signals "cannot calculate" (insufficient/invalid input).
 *
 * Units:
 * - The result is in the same units as the stored weights (in your widget this is typically lbs).
 *
 * @param data Vitals metadata containing current and starting weights.
 * @returns Difference: currentWeight - startingWeight, or null if not calculable.
 */
export function calcStartDiff(data: VitalsMetadata): number | null {
  const weight = toFiniteOrNull(data.weight);
  const startWeight = toFiniteOrNull(data.start_weight);

  if (weight === null || startWeight === null) {
    return null;
  }

  return weight - startWeight;
}
